import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { RouteGuard } from './auth/route-guard'
import { useAuth } from './auth/use-auth'
import { AppFooter } from './components/app-footer'
import { AppShell } from './components/app-shell'
import { RouteAnnouncer } from './components/route-announcer'
import { ToastProvider } from './components/toast'
import { DevModeBanner } from './dev/dev-mode-banner'
import { useMockAuth, useMockBff } from './dev/flags'
import { ModulePlaceholderPage } from './features/modules/module-placeholder-page'
import { PLACEHOLDER_MODULES } from './features/modules/module-registry'
import { BffErrorProvider } from './lib/use-bff-error'
import { AccessDeniedPage } from './pages/access-denied'
import { ErrorPage } from './pages/error'
import { MemberDetailPage } from './pages/member-detail'
import { MembersPage } from './pages/members'
import { NotFoundPage } from './pages/not-found'
import { SignInCallbackPage } from './pages/sign-in-callback'
import { SignInPage } from './pages/sign-in'
import { SignOutPage } from './pages/sign-out'

/**
 * Root router for the Murmuration SPA.
 *
 * Route table (per the MUR "Information Architecture and Navigation" page and the platform-module IA):
 *   /                   - redirect to /members if signed in, else /sign-in
 *   /sign-in            - landing page; CTA initiates Entra redirect
 *   /sign-in/callback   - post-redirect handler; navigates to returnTo
 *   /sign-out           - one-shot route that calls MSAL logoutRedirect
 *   /members            - list view; gated
 *   /members/:id        - detail view; gated
 *   24 module routes    - platform modules in scope but not yet built; gated,
 *                         each renders `ModulePlaceholderPage` from the
 *                         registry. Derived from `PLACEHOLDER_MODULES`, so a
 *                         module added there gets a route with no edit here
 *   /platform/*         - the same, plus `requireMurmurationStaff`. Credit
 *                         union staff never see these in the rail and are
 *                         turned away at the route. Both are presentation:
 *                         the server rejects them independently
 *   /access-denied      - 403 landing; no auto-redirect
 *   /error              - generic error page with correlation id
 *   *                   - 404
 *
 * `/members` replaced `/accounts` when the sidebar took on the platform-module
 * IA: staff navigate by module, and the module is Member Management. The BFF
 * and upstream contract are untouched - `lib/bff-client.ts` still calls
 * `GET /accounts` and `GET /accounts/{id}`, because that is the shape
 * `sm2-account-service` publishes.
 *
 * `RouteGuard` wraps the gated routes; the AppShell renders only inside the
 * guarded subtree so unauth pages (sign-in, access-denied) can use their own
 * minimal chrome (matching the wireframes).
 *
 * `BffErrorProvider` wraps `<Routes>` so all gated routes share the
 * session-expired dialog state and the same 401/403 router. Unauth routes do
 * not call the BFF; the provider is harmless for them and routes outside the
 * React tree still resolve via `useNavigate` because we sit inside
 * `BrowserRouter`.
 *
 * Sub-router pattern: we intentionally do NOT pre-wrap the entire tree in
 * AppShell. Sign-in / access-denied / error are full-page states with their own
 * minimal chrome per the MUR "Mockups" page.
 */
export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <ToastProvider>
        <BffErrorProvider>
          {/* Sets document.title and moves focus to the new h1 on every
              route change. Renders nothing. */}
          <RouteAnnouncer />
          {/* Column layout: the dev banner and the dev footer bracket the
              routed screen, which takes the remaining height. `min-h-0` lets
              the shell's sidebar and content area own their own scrolling
              instead of stretching the page. In production both brackets
              render null, so the shell fills the viewport. */}
          <div className="flex h-full flex-col">
            <DevModeBanner mockAuth={useMockAuth} mockBff={useMockBff} />
            <div className="flex min-h-0 flex-1 flex-col">
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/sign-in" element={<SignInPage />} />
                <Route path="/sign-in/callback" element={<SignInCallbackPage />} />
                <Route path="/sign-out" element={<SignOutPage />} />
                <Route path="/access-denied" element={<AccessDeniedPage />} />
                <Route path="/error" element={<ErrorPage />} />
                <Route
                  path="/members"
                  element={
                    <RouteGuard>
                      <AppShell>
                        <MembersPage />
                      </AppShell>
                    </RouteGuard>
                  }
                />
                <Route
                  path="/members/:id"
                  element={
                    <RouteGuard>
                      <AppShell>
                        <MemberDetailPage />
                      </AppShell>
                    </RouteGuard>
                  }
                />
                {PLACEHOLDER_MODULES.map((module) => (
                  <Route
                    key={module.key}
                    path={module.route}
                    element={
                      <RouteGuard requireMurmurationStaff={module.group === 'platform'}>
                        <AppShell>
                          <ModulePlaceholderPage module={module} />
                        </AppShell>
                      </RouteGuard>
                    }
                  />
                ))}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
            <AppFooter />
          </div>
        </BffErrorProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}

function RootRedirect(): JSX.Element {
  const { signedIn, inProgress } = useAuth()
  if (inProgress) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex h-full items-center justify-center text-text-muted"
      >
        <span className="sr-only">Loading</span>
      </div>
    )
  }
  return <Navigate to={signedIn ? '/members' : '/sign-in'} replace />
}
